import { IconCircleFilled } from '@bbr/icons/circle-filled';
import { IconDiamondsFilled } from '@bbr/icons/diamonds-filled';
import { IconDoubleDiamondFilled } from '@bbr/icons/double-diamond-filled';
import { IconSquareFilled } from '@bbr/icons/square-filled';
import { defineMessages } from '@formatjs/intl';
import { For } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { cn, tv } from 'tailwind-variants';
import type { Trail } from '@bbr/api-client';
import type { IconComponent } from '@bbr/icons';
import type { MessageDescriptor } from '@formatjs/intl';

type DifficultyValue = NonNullable<Trail['difficulty']>[number];

const difficultyIcon: Record<DifficultyValue, IconComponent> = {
	green: IconCircleFilled,
	blue: IconSquareFilled,
	black: IconDiamondsFilled,
	'double-black': IconDoubleDiamondFilled,
};

export const difficultyLabel = defineMessages<DifficultyValue>({
	green: { defaultMessage: 'Green' },
	blue: { defaultMessage: 'Blue' },
	black: { defaultMessage: 'Black' },
	'double-black': { defaultMessage: 'Double-Black' },
});

export function difficultyLabels(t: (def: MessageDescriptor) => string) {
	return Object.entries(difficultyLabel).reduce<Record<DifficultyValue, string>>(
		(acc, [key, val]) => {
			acc[key as DifficultyValue] = t(val);
			return acc;
		},
		{ black: '', blue: '', 'double-black': '', green: '' },
	);
}

const iconColor = tv({
	variants: {
		difficulty: {
			green: 'text-emerald-500',
			// oxlint-disable-next-line tailwindcss/no-hardcoded-colors
			blue: 'text-[oklch(62.3%_0.214_259.815)]',
			black: 'text-black dark:text-white',
			'double-black': 'text-black dark:text-white',
		},
	},
});

type Props = { difficulty: DifficultyValue; class?: string; label: string };

export function Difficulty(props: Props) {
	return (
		<>
			<Dynamic
				component={difficultyIcon[props.difficulty]}
				aria-label={props.label}
				class={cn(iconColor({ difficulty: props.difficulty }), props.class)}
				data-icon={null}
			/>
			<span class="sr-only">{props.label}</span>
		</>
	);
}

export function MultiDifficulty(props: {
	difficulty: Array<DifficultyValue>;
	overlap?: 'tight' | 'tighter';
	labels: Record<DifficultyValue, string>;
}) {
	return (
		<div class="flex items-start">
			<For each={props.difficulty.filter((d, i, self) => self.indexOf(d) === i)}>
				{(difficulty, i) => (
					<div class={multiClasses({ overlap: i() > 0 ? props.overlap : undefined })}>
						<Difficulty
							difficulty={difficulty}
							label={props.labels[difficulty]}
							class="stroke-zinc-100 stroke-[1.25px] dark:stroke-zinc-900"
						/>
					</div>
				)}
			</For>
		</div>
	);
}

const multiClasses = tv({
	base: 'inline-block',
	variants: {
		overlap: {
			tight: '-ms-2.5',
			tighter: '-ms-6',
		},
	},
});
